/**
 * Client-side mirror of the backend Latin-script name policy (MP-6863).
 *
 * Source of truth: api `utils/utils.go` — `ValidateNameWithReason` (~L1438) and the
 * two regexes it uses (`nameRegex` ~L1378, `latinNameRegex` ~L1397). The fixture
 * table in `utils/utils_test.go` ("ValidateNameWithReason") is ported verbatim into
 * `nameValidation.test.ts`; that test is what keeps this file from drifting.
 *
 * Why mirror at all, rather than only mapping the server's error code? Both. The
 * server code is authoritative and is what the error paths map (see
 * `getNameErrorMessage.ts`), but inline validation *before* submit needs the rule
 * locally. Keeping the whole policy behind this one door is what makes drift a
 * visible test failure instead of a silent divergence.
 */

/**
 * Go's RE2 `\s` is exactly `[\t\n\f\r ]`. JavaScript's `\s` is wider — it also
 * matches NBSP (U+00A0), U+2000–200A, U+202F, U+3000 and U+FEFF. Writing the class
 * out literally is load-bearing: with JS `\s` we would accept `"Mary\u00A0Jane"`
 * (a routine paste artifact from Word and PDFs) and hand the guest a 400 from an
 * API that rejects it.
 */
const GO_WHITESPACE = '\\t\\n\\f\\r ';

/**
 * Broad character whitelist — stage 1. Mirrors `nameRegex`:
 *   ^[\p{L}\p{Mn}\p{Mc}'\x{2019}\x{60}\-\s.]+$
 *
 * Unicode letters, nonspacing marks (diacritics), spacing marks (South Asian vowel
 * signs), straight and curly apostrophes, backtick, hyphen, whitespace, period.
 * Enclosing marks (\p{Me}) are excluded on purpose — they never appear in names.
 */
export const NAME_BROAD_PATTERN = new RegExp(
  `^[\\p{L}\\p{Mn}\\p{Mc}'’\`\\-${GO_WHITESPACE}.]+$`,
  'u',
);

/**
 * Latin-script narrowing — stage 2. Mirrors `latinNameRegex`:
 *   ^[\p{Latin}\p{Mn}\p{Mc}\x{02BB}-\x{02BF}'\x{2019}\x{60}\-\s.]+$
 *
 * Two things here are easy to get wrong and both are deliberate:
 *
 *  - `\p{Script=Latin}`, never `\p{Script_Extensions=Latin}`. Go's `\p{Latin}` is
 *    Script=; Script_Extensions is laxer and would quietly accept more than the
 *    server does.
 *  - `ʻ-ʿ` are modifier letters carrying Script=**Common**, not Latin, so
 *    `\p{Script=Latin}` alone rejects names that are unambiguously Latin script:
 *    the Hawaiian ʻokina (`Haʻaheo`), the Uzbek/Kazakh apostrophe (`Gʼafur`), and
 *    the half rings of romanized Arabic (`Saʿid`). The api shipped that bug and
 *    caught it in review; the same trap is waiting here.
 *
 * `\p{Mn}`/`\p{Mc}` are listed separately because combining marks are
 * Script=Inherited — that is what keeps decomposed `Nguyễn` (e + U+0303 + U+0301)
 * valid alongside the precomposed U+1EC5 spelling.
 */
export const NAME_LATIN_PATTERN = new RegExp(
  `^[\\p{Script=Latin}\\p{Mn}\\p{Mc}\\u02BB-\\u02BF'’\`\\-${GO_WHITESPACE}.]+$`,
  'u',
);

/**
 * Fullwidth forms (U+FF00–FFEF) and Mathematical Alphanumeric Symbols
 * (U+1D400–1D7FF). Mirrors `ContainsSuspiciousUnicode`.
 *
 * These are Script=Latin, so `NAME_LATIN_PATTERN` accepts them — this check is the
 * only thing rejecting `ａｂｃ` and `𝐯𝐞𝐫𝐢𝐟𝐲`, and it must run in stage 1.
 *
 * Built from a string rather than written as a regex literal so the bounds stay
 * as `\uFF00`/`\uFFEF` escapes. As a literal the range renders as the fullwidth
 * characters themselves, which no reviewer can check by eye and an editor can
 * silently mangle. Pinned by `.source` in the tests for the same reason.
 */
export const SUSPICIOUS_UNICODE = new RegExp('[\\uFF00-\\uFFEF]|[\\u{1D400}-\\u{1D7FF}]', 'u');

/** Mirrors `StringContainsExcludedNameTerms` — a case-insensitive substring match. */
const EXCLUDED_NAME_TERMS = ['wheelbase', 'outdoorsy', 'notification', 'support'];

/**
 * Why a guest's name was rejected. The rejection values are the backend's own
 * `code` strings, so one type carries both the locally-computed verdict and the
 * code read off a 400 — and one mapper (`getNameErrorMessage`) serves both.
 */
export enum ENameRejection {
  Ok = 'ok',
  /** Digits, symbols, fullwidth/math letterforms, or an excluded term. */
  InvalidCharacters = 'NameInvalidCharacters',
  /** Every character is legal, but the name isn't written in Latin script. */
  NotLatinScript = 'NameNotLatinScript',
  /** The api refuses to change a name that a government ID check has locked. */
  LockedByVerification = 'NameLockedByVerification',
}

/**
 * Trim and NFC-normalize. Every surface runs its value through this at seed,
 * validate, and persist time, so the string that gets measured is the string that
 * gets stored.
 *
 * The `trim()` is load-bearing: the length checks run on the canonical value, so
 * `" a "` must not clear a min-2 rule and then persist as `"a"` (MP-6871).
 *
 * The NFC normalization is now belt-and-braces rather than required for
 * correctness. It mattered when the rule was a range-based regex that only
 * matched precomposed diacritics; `NAME_LATIN_PATTERN` lists `\p{Mn}`/`\p{Mc}`
 * explicitly, which is why decomposed `Nguyẽ́n` passes on its own. Keeping it
 * means the persisted spelling stays stable regardless of how it was typed.
 */
export const toCanonicalName = (value: string): string => value.trim().normalize('NFC');

/**
 * UTF-8 byte length — the unit the API truncates names in. Not `.length`, which
 * counts UTF-16 code units and undercounts every non-ASCII letter.
 *
 * Counted per code point rather than via TextEncoder, which jsdom doesn't
 * provide. Same result: a lone surrogate comes out as 3, as TextEncoder's
 * U+FFFD replacement does.
 */
export const utf8ByteLength = (value: string): number => {
  let bytes = 0;
  for (const char of value) {
    const codePoint = char.codePointAt(0) ?? 0;
    if (codePoint < 0x80) bytes += 1;
    else if (codePoint < 0x800) bytes += 2;
    else if (codePoint < 0x10000) bytes += 3;
    else bytes += 4;
  }
  return bytes;
};

/**
 * Split a "Full name" into first + last for the sign-up API. Middle tokens stay
 * attached to `lastName` — three-token names are the norm for Vietnamese and many
 * other cultures (Nguyễn Văn A → Nguyễn / Văn A, not Nguyễn / A).
 *
 * Splits on JavaScript's `\s+`, which is wider than the whitespace the name
 * patterns admit. That asymmetry is intentional and matches the server: a pasted
 * `"Mary\u00A0Jane"` splits cleanly into two accepted halves rather than being
 * rejected whole.
 */
export const splitFullName = (fullName: string): { firstName: string; lastName: string } => {
  const parts = fullName.trim().split(/\s+/);
  const firstName = parts.shift() ?? '';
  const lastName = parts.join(' ');
  return { firstName, lastName };
};

/**
 * Classify one name field (a first name or a last name).
 *
 * Mirrors `ValidateNameWithReason`, including the ordering: character failures are
 * reported *before* the script check, so `Harry1` is a character problem rather
 * than a script problem. Telling a guest to find their passport when the real
 * issue is a digit would be actively misleading.
 *
 * An empty string is `Ok` here, exactly as on the server — required-ness is a
 * separate rule that the form layer owns.
 */
export const classifyName = (value: string): ENameRejection => {
  if (value === '') return ENameRejection.Ok;

  // Both checks run against the lowercased copy, matching
  // `StringContainsExcludedNameTerms`, which builds `normalizedText` for the term
  // match and then hands that same lowercased string to `ContainsSuspiciousUnicode`.
  // Passing the raw value here would read more natural and would be a divergence.
  // (No behavioral difference today — fullwidth uppercase lowercases within
  // U+FF00–FFEF and math alphanumerics have no case mappings — but parity is the
  // point of this file.)
  //
  // The lowercasing itself is not quite parity, and can't be: Go's strings.ToLower
  // applies simple case mapping, JS toLowerCase applies full case mapping. They
  // disagree on U+0130 — "Notİfication" lowercases to "notification" in Go and to
  // "noti̇fication" (with a combining U+0307) here, so the server treats it as an
  // excluded term and we don't. Left alone deliberately: the input is contrived,
  // and the server still rejects it, so the only cost is one wasted round trip.
  const lowered = value.toLowerCase();
  const failsCharacterChecks =
    EXCLUDED_NAME_TERMS.some(term => lowered.includes(term)) || SUSPICIOUS_UNICODE.test(lowered);

  if (!NAME_BROAD_PATTERN.test(value) || failsCharacterChecks) {
    return ENameRejection.InvalidCharacters;
  }
  if (!NAME_LATIN_PATTERN.test(value)) {
    return ENameRejection.NotLatinScript;
  }
  return ENameRejection.Ok;
};

/**
 * Classify a single "Full name" field the way the server will once it splits the
 * value — `validations.FirstAndLastName` checks `first_name`, then `last_name`, and
 * reports the first failure.
 *
 * Classifying the whole string instead would disagree in two real cases:
 *   - `"Иван Bob5"` — whole-string reports invalid characters (the digit trips
 *     stage 1), but the server reaches `first_name` first and reports non-Latin.
 *   - `"Mary\u00A0Jane"` — the server splits on NBSP and accepts both halves, but
 *     whole-string matching rejects it, since NBSP isn't in the Go whitespace class.
 */
export const classifyFullName = (value: string): ENameRejection => {
  const { firstName, lastName } = splitFullName(value);
  const firstVerdict = classifyName(firstName);
  return firstVerdict === ENameRejection.Ok ? classifyName(lastName) : firstVerdict;
};
